import type { Api } from "api-client";
import { format, getDate } from "date-fns";
import { sort } from "fast-sort";
import { makeAutoObservable } from "mobx";
import { assert, groupBy } from "utils";

import {
  getCalendarCellDate,
  getCalendarWeekdayLabels,
  isCalendarCellToday,
} from "src/calendar/calendarWeeks";
import { dateFromDateUrl } from "src/calendar/utils";

// ============================================================================
// Types
// ============================================================================

export type ProgramGridMode = { type: "sequential" } | { type: "dated" };

type ProgramWorkout = Api.GetProgram.Output.ProgramWorkout;

/**
 * Grouped workouts type - key is either day index (number) or date string.
 * Strategy determines which grouping is used.
 */
export type GroupedWorkouts = Record<number | string, ProgramWorkout[]>;

/**
 * Provider interface for accessing program workouts.
 */
export interface GridWorkoutProvider {
  programWorkouts: ProgramWorkout[];
}

// ============================================================================
// GridStrategy Interface
// ============================================================================

export interface GridStrategy {
  readonly mode: ProgramGridMode;

  /**
   * Grouped workouts - computed by strategy.
   * Sequential: groups by day index
   * Dated: groups by date string (yyyy-MM-dd)
   */
  readonly workoutsGrouped: GroupedWorkouts;

  /**
   * Get workouts for a specific key from grouped data.
   * Sorted by order ascending.
   */
  getWorkoutsForKey(key: number | string): ProgramWorkout[];

  getWorkoutsForCell(weekIndex: number, dayOfWeek: number): ProgramWorkout[];

  /**
   * Get ISO date string for a cell (yyyy-MM-dd).
   * Only valid in dated mode - throws assertion error in sequential mode.
   */
  getCellDate(cellId: number): string;

  getHeaderLabels(): Array<{ primary: string; secondary?: string }>;

  getCellDateLabel(weekIndex: number, dayOfWeek: number): string | null;

  isCellToday(weekIndex: number, dayOfWeek: number): boolean;

  getGroupKey(): "day" | "date";

  getGroup(programWorkout: ProgramWorkout): ProgramWorkout[];

  getGroupByCellId(cellId: number): ProgramWorkout[];

  getGroupValueForCellId(cellId: number): number | string;
}

// ============================================================================
// SequentialGridStrategy
// ============================================================================

export class SequentialGridStrategy implements GridStrategy {
  readonly mode: ProgramGridMode = { type: "sequential" };
  readonly provider: GridWorkoutProvider;
  private readonly locale: string;

  constructor(provider: GridWorkoutProvider, locale: string) {
    this.provider = provider;
    this.locale = locale;
    makeAutoObservable(this, { provider: false });
  }

  get workoutsGrouped(): GroupedWorkouts {
    return groupBy(this.provider.programWorkouts, (pw) => {
      return pw.day;
    });
  }

  getWorkoutsForKey(key: number | string): ProgramWorkout[] {
    const workouts = this.workoutsGrouped[key] ?? [];
    return sort(workouts).asc("order");
  }

  getWorkoutsForCell(weekIndex: number, dayOfWeek: number): ProgramWorkout[] {
    const dayIndex = weekIndex * 7 + dayOfWeek;
    return this.getWorkoutsForKey(dayIndex);
  }

  getCellDate(_cellId: number): string {
    assert(false, "getCellDate should only be called in dated mode");
  }

  getHeaderLabels(): Array<{ primary: string; secondary?: string }> {
    return Array.from({ length: 7 }, (_, i) => {
      return { primary: String(i + 1) };
    });
  }

  getCellDateLabel(_weekIndex: number, _dayOfWeek: number): string | null {
    return null;
  }

  isCellToday(_weekIndex: number, _dayOfWeek: number): boolean {
    return false;
  }

  getGroupKey(): "day" | "date" {
    return "day";
  }

  getGroup(programWorkout: ProgramWorkout): ProgramWorkout[] {
    return this.workoutsGrouped[programWorkout.day] ?? [];
  }

  getGroupByCellId(cellId: number): ProgramWorkout[] {
    return this.workoutsGrouped[cellId] ?? [];
  }

  getGroupValueForCellId(cellId: number): number {
    return cellId;
  }
}

// ============================================================================
// DatedGridStrategy
// ============================================================================

export class DatedGridStrategy implements GridStrategy {
  readonly mode: ProgramGridMode = { type: "dated" };
  readonly provider: GridWorkoutProvider;
  private readonly locale: string;

  constructor(provider: GridWorkoutProvider, locale: string) {
    this.provider = provider;
    this.locale = locale;
    makeAutoObservable(this, { provider: false });
  }

  get workoutsGrouped(): GroupedWorkouts {
    return groupBy(this.provider.programWorkouts, (pw) => {
      return pw.date ?? "";
    });
  }

  getWorkoutsForKey(key: number | string): ProgramWorkout[] {
    const workouts = this.workoutsGrouped[key] ?? [];
    return sort(workouts).asc("order");
  }

  getWorkoutsForCell(weekIndex: number, dayOfWeek: number): ProgramWorkout[] {
    return this.getWorkoutsForKey(this.getCellDate(weekIndex * 7 + dayOfWeek));
  }

  getCellDate(cellId: number): string {
    return getCalendarCellDate(cellId);
  }

  getHeaderLabels(): Array<{ primary: string; secondary?: string }> {
    return getCalendarWeekdayLabels(this.locale);
  }

  getCellDateLabel(weekIndex: number, dayOfWeek: number): string | null {
    const date = dateFromDateUrl(this.getCellDate(weekIndex * 7 + dayOfWeek));
    const dayOfMonth = getDate(date);

    if (dayOfMonth === 1) {
      return format(date, "MMM d");
    }

    return String(dayOfMonth);
  }

  isCellToday(weekIndex: number, dayOfWeek: number): boolean {
    return isCalendarCellToday(weekIndex * 7 + dayOfWeek);
  }

  getGroupKey(): "day" | "date" {
    return "date";
  }

  getGroup(programWorkout: ProgramWorkout): ProgramWorkout[] {
    if (programWorkout.date === null) {
      return [];
    }

    return this.workoutsGrouped[programWorkout.date] ?? [];
  }

  getGroupByCellId(cellId: number): ProgramWorkout[] {
    return this.workoutsGrouped[this.getCellDate(cellId)] ?? [];
  }

  getGroupValueForCellId(cellId: number): string {
    return this.getCellDate(cellId);
  }
}

// ============================================================================
// Factory
// ============================================================================

export function createGridStrategy(
  provider: GridWorkoutProvider,
  locale: string,
  mode?: ProgramGridMode,
): GridStrategy {
  if (!mode || mode.type === "sequential") {
    return new SequentialGridStrategy(provider, locale);
  }

  return new DatedGridStrategy(provider, locale);
}
